import React, { useEffect, useMemo, useState } from 'react';
import { InfoIcon, XIcon } from '@phosphor-icons/react';
import { Chip, Loader, EmptyState, SearchInput } from '@origo/origo-ui';
import { Toaster } from '@origo/origo-components';
import { NaboListeService, MatrikkelEnhet, type AvvikPort } from '@origo/matrikkel-services';
import { createArcgisAvvikRepository, anvendDeltaerPåEnhetstre } from '@origo/seksjon';
import { useAppSelector, selectGdoToken } from '../../store/storeHooks';
import { useMapViewContext } from '@origo/map-context';
import { createLogger } from '@origo/logger';
import type { EiendomsvelgerTarget } from './eiendomsvelgerTarget';
import styles from './Eiendommer.module.css';

const log = createLogger('Eiendommer');

/** Skeleton-lastetilstand: antall chip-plassholdere pr. gate-rad. */
const SKELETON_ROWS = [8, 6, 7, 5, 4];

/** Endrings-oppsummering for økten — antall valgte nå, samt hvor mange som er
 *  lagt til / fjernet siden grunnsettet ble lastet. Drives oppover så panelet
 *  kan vise «X lagt til, Y fjernet» i footeren. */
export interface EiendommerSummary {
    total: number;
    added: number;
    removed: number;
    /** Sant mens en delta-skriving pågår, så footeren kan vise «Lagrer…». */
    saving: boolean;
}

interface EiendommerProps {
    /** Hierarkinivået eiendommene knyttes til. Se buildEiendomsvelgerTarget. */
    target: EiendomsvelgerTarget;
    eiendommerUrl?: string;
    /** Eldre flat-visning (egendefinert inline-seksjon). Default er gruppert. */
    flat?: boolean;
    showEmptyState?: boolean;
    /** Rapporterer status oppover: null mens lasten pågår, ellers oppsummering. */
    onSummaryChange?: (summary: EiendommerSummary | null) => void;
}

type NaboListeServiceWithClear = NaboListeService & {
    clearSelection?: () => Promise<void>;
};

// Hjelpefunksjon for å hente gatenavn og nummer/bokstav
function splitAdresse(adresse?: string) {
    if (!adresse) return { gate: 'Uten gate', nummer: '' };
    const match = adresse.match(/^(.+?)\s*([0-9]+[A-ZÆØÅa-zæøå]*)?$/);
    return {
        gate: match?.[1]?.trim() || 'Uten gate',
        nummer: match?.[2] || ''
    };
}

function getMatrikkelFallbackLabel(eiendom: any): string {
    if (eiendom.gardsnummer) return `${eiendom.gardsnummer}/${eiendom.bruksnummer}`;
    return `${eiendom.gardsnr}/${eiendom.bruksnr}`;
}

function getEiendomDisplayLabel(eiendom: MatrikkelEnhet): { gate: string; label: string } {
    const { gate, nummer } = splitAdresse(eiendom.adresse);
    if (nummer) {
        return { gate, label: nummer };
    }
    return { gate, label: getMatrikkelFallbackLabel(eiendom) };
}

type GateEntry = { label: string; eiendommer: MatrikkelEnhet[] };
type GateGroup = { gate: string; entries: GateEntry[] };

function groupAndSortEiendommer(eiendommer: MatrikkelEnhet[]): GateGroup[] {
    const grouped: Record<string, Record<string, MatrikkelEnhet[]>> = {};
    eiendommer.forEach(e => {
        const { gate, label } = getEiendomDisplayLabel(e);
        if (!grouped[gate]) grouped[gate] = {};
        if (!grouped[gate][label]) grouped[gate][label] = [];
        grouped[gate][label].push(e);
    });
    return Object.entries(grouped)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([gate, labels]) => ({
            gate,
            entries: Object.entries(labels)
                .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
                .map(([label, eiendommer]) => ({ label, eiendommer })),
        }));
}

/** Stabil nøkkel for en matrikkelenhet — for delta-beregning (lagt til / fjernet). */
function eiendomKey(e: MatrikkelEnhet): string {
    const anyE = e as any;
    return String(anyE.objectid ?? anyE.objectId ?? anyE.id ?? `${anyE.gardsnummer ?? anyE.gardsnr}/${anyE.bruksnummer ?? anyE.bruksnr}/${e.adresse ?? ''}`);
}

const Eiendommer: React.FC<EiendommerProps> = ({ target, eiendommerUrl, flat = false, showEmptyState = false, onSummaryChange }) => {
    const { mapView } = useMapViewContext();
    const naboServiceRef = React.useRef<NaboListeService | null>(null);
    const [eiendommer, setEiendommer] = useState<MatrikkelEnhet[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    // Angre-toast: matrikkelenhetene som nettopp ble fjernet, så de kan legges
    // tilbake hvis saksbehandleren angrer.
    const [undoToast, setUndoToast] = useState<{ eiendommer: MatrikkelEnhet[]; label: string } | null>(null);
    // Grunnsettet (auto-inkluderte) snapshottes ved første ferdig-lasting, så vi
    // kan vise hvor mange saksbehandleren har lagt til / fjernet i økten.
    const baselineRef = React.useRef<Set<string> | null>(null);
    const token = useAppSelector(selectGdoToken) || '';
    const apiUrl = 'https://geoeiendom.no/api/';

    // Bygg det løse «feature»-objektet NaboListeService forventer, med stabil
    // identitet så effekten ikke kjører på nytt (og lager ny tjeneste-instans)
    // ved hver render. NaboListeService leser kun .geometry + .attributes.
    //
    // Nøkkelen er (omradeId, omradetype) — de identifiserer entydig det valgte
    // nivået. Vi kan IKKE ha target.geometry som dep: for delområdegruppe er
    // geometrien en union som lages på nytt ved hver recompute, og en ny
    // geometri-referanse ville rekonstruere tjenesten midt i en økt (→ activeOmradeId
    // transient undefined → delta-rader uten omradeid). Geometrien leses ved bygging;
    // den endrer seg kun logisk når omradeId gjør det.
    const feature = useMemo(() => ({
        geometry: target.geometry ?? null,
        attributes: { omradeid: target.omradeId, omradetype: target.omradetype },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [target.omradeId, target.omradetype]);

    useEffect(() => {
        if (!mapView || !feature) return;

        // `cancelled` hindrer at en utgående instans (hvis effekten kjører på
        // nytt) skriver state. `loading` styres av at SELVE lasten resolver —
        // ikke av et enkelt event — så en stale `clearSelection([])`-dispatch
        // fra forrige instans ikke flasher empty state før dataene kommer.
        let cancelled = false;
        setLoading(true);
        setSaving(false);
        setEiendommer([]);
        baselineRef.current = null;
        // Avviksdomenet (drømmeplan-spesifikt) injiseres i den generiske
        // nabolisten (etappe 2A, B3): reglene + persisteringen bor i
        // @origo/seksjon; appen — som avhenger av begge pakkene — eier
        // koblingen. matrikkel-services forblir uten seksjon-avhengighet.
        const avvikPort: AvvikPort | undefined = eiendommerUrl
            ? {
                  ...createArcgisAvvikRepository({
                      eiendommerUrl,
                      ønsketWkid: () => mapView.spatialReference?.wkid,
                  }),
                  anvendPåEnhetstre: anvendDeltaerPåEnhetstre,
              }
            : undefined;
        const svc = new NaboListeService(mapView, token, apiUrl, eiendommerUrl ?? '', avvikPort);
        naboServiceRef.current = svc;

        // Direkte callbacks i stedet for globalt window-event: siden `svc` og
        // disse callbackene tilhører samme effekt-kjøring, kan ikke lista desynce
        // ved rekonstruksjon (klikk-handler og mottaker er alltid samme generasjon).
        svc.setListeners({
            onPropertiesChange: (properties) => {
                if (cancelled) return;
                setEiendommer(properties);
            },
            onSavingChange: (isSaving) => {
                if (cancelled) return;
                setSaving(isSaving);
            },
        });

        log.debug('Henter eiendommer for feature:', feature);
        (async () => {
            try {
                // NaboListeService leser kun .geometry + .attributes fra «graphic»;
                // vi sender et lett objekt, ikke en ekte esri Graphic.
                const loadPromise = feature.geometry
                    ? svc.getIntersectingProperties([feature as never], token, apiUrl)
                    : feature.attributes?.omradeid != null
                        ? svc.loadPersistedProperties(
                            feature.attributes.omradeid,
                            feature.attributes?.omradetype ?? 'egendefinert',
                          )
                        : Promise.resolve();
                svc.activatePropertySelector();
                await loadPromise;
            } catch (err) {
                log.error('Kunne ikke laste eiendommer:', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            svc.setListeners({});
            svc.deactivatePropertySelector();
            void (svc as NaboListeServiceWithClear).clearSelection?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapView, feature]);

    // Snapshot grunnsettet første gang lista er på plass, og rapporter delta.
    // Mens lasten pågår: rapporter null så footeren viser «Laster inn…».
    useEffect(() => {
        if (loading) {
            onSummaryChange?.(null);
            return;
        }
        const keys = eiendommer.map(eiendomKey);
        if (baselineRef.current === null) {
            baselineRef.current = new Set(keys);
        }
        if (!onSummaryChange) return;
        const baseline = baselineRef.current;
        const now = new Set(keys);
        let added = 0;
        now.forEach((k) => { if (!baseline.has(k)) added++; });
        let removed = 0;
        baseline.forEach((k) => { if (!now.has(k)) removed++; });
        onSummaryChange({ total: eiendommer.length, added, removed, saving });
    }, [eiendommer, loading, saving, onSummaryChange]);

    // Cmd/Ctrl+Z angrer siste fjerning mens angre-toasten er aktiv.
    useEffect(() => {
        if (!undoToast) return;
        const onKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
                e.preventDefault();
                void handleUndoRemove();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [undoToast]);

    const grouped = useMemo(() => groupAndSortEiendommer(eiendommer), [eiendommer]);

    // Filtrer på gatenavn eller nummer/bnr — tom-søk gir hele lista.
    const filtered = useMemo<GateGroup[]>(() => {
        const q = search.trim().toLowerCase();
        if (!q) return grouped;
        return grouped
            .map(({ gate, entries }) => {
                const gateMatch = gate.toLowerCase().includes(q);
                const keptEntries = gateMatch
                    ? entries
                    : entries.filter((e) => e.label.toLowerCase().includes(q));
                return { gate, entries: keptEntries };
            })
            .filter((g) => g.entries.length > 0);
    }, [grouped, search]);

    function handleAdresseClick(gate: string, nr: string) {
        const valgtEiendom = eiendommer.find(e => {
            const { gate: eGate, label: eLabel } = getEiendomDisplayLabel(e);
            return eGate === gate && eLabel === nr;
        });
        if (valgtEiendom) {
            naboServiceRef.current?.gotoEiendom(valgtEiendom);
        }
    }

    async function handleRemoveEiendom(entryEiendommer: MatrikkelEnhet[], label?: string) {
        await naboServiceRef.current?.removeMatrikkelEnheter(entryEiendommer);
        if (label) setUndoToast({ eiendommer: entryEiendommer, label });
    }

    async function handleUndoRemove() {
        if (!undoToast) return;
        await naboServiceRef.current?.addMatrikkelEnheter(undoToast.eiendommer);
        setUndoToast(null);
    }

    // ── Eldre flat-visning (egendefinert inline-seksjon) ──
    if (flat) {
        return (
            <div className={styles.root}>
                <p className={styles.help}>
                    Klikk på en teig i kartet for å legge til eller fjerne. Hold nede shift for å legge til/fjerne alle teiger innen en matrikkelenhet.
                </p>
                {loading && (
                    <div className={styles.loading}>
                        <Loader size="sm" />
                        <span>Laster eiendommer…</span>
                    </div>
                )}
                {!loading && grouped.length === 0 && showEmptyState && (
                    <EmptyState
                        icon={<InfoIcon size={24} aria-hidden="true" />}
                        title="Ingen eiendommer lagt til"
                    />
                )}
                {!loading && grouped.length > 0 && (
                    <div className={styles.list}>
                        {grouped.flatMap(({ gate, entries }) =>
                            entries.map(({ label, eiendommer: entryEiendommer }) => (
                                <Chip
                                    key={`${gate}-${label}`}
                                    className={styles.chip}
                                    onClick={() => handleAdresseClick(gate, label)}
                                    onRemove={() => void handleRemoveEiendom(entryEiendommer)}
                                    removeLabel={`Fjern ${gate} ${label}`}
                                >
                                    {gate} {label}
                                </Chip>
                            ))
                        )}
                    </div>
                )}
            </div>
        );
    }

    // ── Gruppert visning (delt map-first eiendomsvelger) ──
    return (
        <div className={styles.root}>
            <SearchInput
                label="Søk opp i listen"
                value={search}
                onChange={setSearch}
                placeholder="Søk…"
            />

            {loading && (
                <div className={styles.skeleton} role="status" aria-label="Laster eiendommer">
                    {SKELETON_ROWS.map((count, i) => (
                        <div key={i} className={styles.gateGroup} aria-hidden="true">
                            <div className={styles.skeletonGate} />
                            <div className={styles.entries}>
                                {Array.from({ length: count }).map((_, j) => (
                                    <div key={j} className={styles.skeletonChip} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!loading && grouped.length === 0 && showEmptyState && (
                <EmptyState
                    icon={<InfoIcon size={32} aria-hidden="true" />}
                    title="Ingen eiendommer i planområdet ennå"
                    description="Klikk en teig i kartet for å legge til eiendommer manuelt."
                />
            )}

            {!loading && grouped.length > 0 && filtered.length === 0 && (
                <EmptyState
                    icon={<InfoIcon size={32} aria-hidden="true" />}
                    title={`Ingen treff på «${search.trim()}»`}
                    description="Prøv et annet gatenavn eller nummer."
                />
            )}

            {!loading && filtered.length > 0 && (
                <div className={styles.groupedList}>
                    {filtered.map(({ gate, entries }) => (
                        <div key={gate} className={styles.gateGroup}>
                            <div className={styles.gateHeader}>
                                <span className={styles.gateLabel}>{gate}</span>
                                <span className={styles.countBadge}>{entries.length}</span>
                            </div>
                            <div className={styles.entries}>
                                {entries.map(({ label, eiendommer: entryEiendommer }) => (
                                    <span key={label} className={styles.numberChip}>
                                        <button
                                            type="button"
                                            className={styles.numberBtn}
                                            onClick={() => handleAdresseClick(gate, label)}
                                        >
                                            {label}
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.removeBtn}
                                            aria-label={`Fjern ${gate} ${label}`}
                                            onClick={() => void handleRemoveEiendom(entryEiendommer, `${gate} ${label}`)}
                                        >
                                            <XIcon size={14} weight="bold" aria-hidden="true" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Toaster
                open={undoToast !== null}
                variant="info"
                autoCloseDuration={6000}
                onClose={() => setUndoToast(null)}
                message={
                    undoToast && (
                        <span className={styles.undoToast}>
                            {undoToast.label} fjernet
                            <button
                                type="button"
                                className={styles.undoButton}
                                onClick={() => void handleUndoRemove()}
                            >
                                Angre
                            </button>
                        </span>
                    )
                }
            />
        </div>
    );
};

export default Eiendommer;
